import React, { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Network, Plus, UserPlus, UserMinus, Check, X, Vote } from "lucide-react";
import { toast } from "sonner";

export default function ProTribus() {
  const [tribus, setTribus] = useState([]);
  const [newName, setNewName] = useState("");
  const [addFor, setAddFor] = useState(null); // tribu id
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);

  const load = () => api.get("/pro-tribus").then((r) => setTribus(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!newName.trim()) return;
    try { await api.post("/pro-tribus", { name: newName }); setNewName(""); toast.success("Tribu créée"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const search = async () => {
    const { data } = await api.get("/companies/search", { params: q ? { q } : {} });
    setResults(data);
  };

  const propose = async (tid, type, company_id) => {
    try {
      await api.post(`/pro-tribus/${tid}/propose`, { type, target_company_id: company_id });
      toast.success(type === "add" ? "Proposition d'ajout soumise au vote" : "Proposition d'exclusion soumise au vote");
      setAddFor(null); setResults([]); setQ("");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const vote = async (tid, pid, approve) => {
    try {
      const { data } = await api.post(`/pro-tribus/${tid}/proposals/${pid}/vote`, { approve });
      toast.success(data.status === "approved" ? "Adopté à l'unanimité" : data.status === "rejected" ? "Proposition rejetée" : "Vote enregistré");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
          <Network className="h-6 w-6 text-primary" /> Tribus Professionnelles
        </h1>
        <p className="text-muted-foreground text-sm">Réseaux collaboratifs entre confrères. Intégration et exclusion à l'unanimité.</p>
      </div>

      <Card className="p-4 rounded-md shadow-none flex flex-wrap gap-2 items-end">
        <div className="flex-1 min-w-[200px]">
          <label className="text-xs text-muted-foreground">Nouvelle tribu</label>
          <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Ex: Réseau Côte-d'Or" className="mt-1" data-testid="protribu-name" />
        </div>
        <Button onClick={create} className="gap-2" data-testid="protribu-create"><Plus className="h-4 w-4" /> Créer</Button>
      </Card>

      <div className="space-y-4" data-testid="protribu-list">
        {tribus.length === 0 && <p className="text-sm text-muted-foreground">Aucune tribu. Créez-en une pour partager vos surcharges.</p>}
        {tribus.map((t) => (
          <Card key={t.id} className="p-4 rounded-md shadow-none" data-testid={`protribu-${t.id}`}>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display font-semibold">{t.name}</h2>
              <Button size="sm" variant="outline" className="gap-1" onClick={() => { setAddFor(t.id); setResults([]); setQ(""); }} data-testid={`protribu-add-member-${t.id}`}>
                <UserPlus className="h-4 w-4" /> Proposer un membre
              </Button>
            </div>

            <div className="flex flex-wrap gap-2 mb-3">
              {t.members.map((m) => (
                <div key={m.id} className="flex items-center gap-2 border border-border rounded-full pl-3 pr-1 py-1 text-sm">
                  {m.company_name}
                  <Button size="icon" variant="ghost" className="h-6 w-6" title="Proposer l'exclusion" onClick={() => propose(t.id, "remove", m.id)} data-testid={`protribu-remove-${m.id}`}>
                    <UserMinus className="h-3 w-3 text-destructive" />
                  </Button>
                </div>
              ))}
            </div>

            {t.proposals.length > 0 && (
              <div className="space-y-2 border-t border-border pt-3">
                <p className="text-xs font-medium text-muted-foreground flex items-center gap-1"><Vote className="h-3 w-3" /> Votes en cours</p>
                {t.proposals.map((p) => {
                  const yes = Object.values(p.votes).filter((v) => v === true).length;
                  return (
                    <div key={p.id} className="flex items-center justify-between bg-secondary rounded-md px-3 py-2 text-sm" data-testid={`proposal-${p.id}`}>
                      <span>
                        {p.type === "add" ? "Ajouter" : "Exclure"} <strong>{p.target_name}</strong>
                        <Badge variant="outline" className="ml-2 text-[10px]">{yes}/{p.voters.length} pour</Badge>
                      </span>
                      {p.my_vote == null ? (
                        <div className="flex gap-1">
                          <Button size="sm" className="h-7 gap-1" onClick={() => vote(t.id, p.id, true)} data-testid={`vote-yes-${p.id}`}><Check className="h-3 w-3" /> Pour</Button>
                          <Button size="sm" variant="outline" className="h-7 gap-1" onClick={() => vote(t.id, p.id, false)} data-testid={`vote-no-${p.id}`}><X className="h-3 w-3" /> Contre</Button>
                        </div>
                      ) : (
                        <Badge variant="outline">{p.my_vote ? "Vous : Pour" : "Vous : Contre"}</Badge>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        ))}
      </div>

      <Dialog open={!!addFor} onOpenChange={(o) => !o && setAddFor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Proposer un confrère</DialogTitle></DialogHeader>
          <div className="flex gap-2">
            <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} placeholder="Nom ou email exact" data-testid="protribu-search-input" />
            <Button onClick={search} data-testid="protribu-search-btn">Rechercher</Button>
          </div>
          <div className="space-y-2 mt-2 max-h-72 overflow-auto">
            {results.map((c) => (
              <div key={c.id} className="flex items-center justify-between border border-border rounded-md px-3 py-2">
                <div><p className="text-sm font-medium">{c.company_name}</p><p className="text-xs text-muted-foreground">{c.rate_per_km} €/km · prise en charge {c.base_fare} €</p></div>
                <Button size="sm" onClick={() => propose(addFor, "add", c.id)} data-testid={`protribu-propose-${c.id}`}>Proposer</Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
